import React, {FunctionComponent, useMemo, useRef} from "react";
import {css, cx} from "@panda/css";

import {Col} from "@pg-design/grid-module";
import {Loader} from "@pg-design/loader-module";
import {Pagination, PaginationSize} from "@pg-design/pagination-module";
import {useIsMounted} from "@pg-mono/hooks";
import {RequestState} from "@pg-mono/request-state";
import {useUserDevice} from "@pg-mono/user-device";

import {ColumnList} from "../../../atoms/ColumnList";
import {OfferBox} from "../../../offer/components/offer_box/OfferBox";
import {OfferDisplayType} from "../../../offer/helpers/OfferDisplayType";
import {showOfferModal} from "../../../offer/state/offer_modals_slice";
import {offerBoxTracking} from "../../../offer/tracking/offer_box_gtm_events";
import {IOfferModalName} from "../../../offer/types/OfferModalName";
import {IOfferModalOffer} from "../../../offer/types/OfferModalOffer";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {useGetVendorDetailAllActiveOffersQuery} from "../../api/get_vendor_detail_all_active_offers";
import {VENDOR_DETAIL_OFFERS_MAX_PAGE_SIZE} from "../../constants/vendor_detail_offers_max_page_size";
import {setActiveOffersPage} from "../../state/vendor_offer_list_slice";
import {convertToVendorDetailViewOffersPayload} from "../../utils/convert_to_vendor_detail_view_offers_payload";
import {VendorDetailOfferModal} from "./VendorDetailOfferModal";

interface IProps {
    vendor: {
        id: number;
        stats: {
            offers_count_for_sale: number;
            properties_count_for_sale: number;
        };
    };
    offersInRegionRequest: RequestState;
}

const modalName: IOfferModalName = "mainOfferModal";

const ACTIVE_OFFERS_TO_SHOW = 6;
//array with index numbers, length is equal to ACTIVE_OFFERS_TO_SHOW
const baseOffersArrayIndexesToShow = Array.from(Array(ACTIVE_OFFERS_TO_SHOW).keys());

export const VendorDetailActiveOffers: FunctionComponent<IProps> = React.memo((props) => {
    const {offersInRegionRequest, vendor} = props;
    const dispatch = useAppDispatch();
    const {isMobile} = useUserDevice();
    const isMounted = useIsMounted();
    const listRef = useRef<HTMLDivElement>(null);

    const page = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.page);
    const selectedRegion = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.region);
    const selectedOfferType = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.offerType);
    const selectedSort = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.sort);

    const {data: queryData, isFetching} = useGetVendorDetailAllActiveOffersQuery({
        ...convertToVendorDetailViewOffersPayload({
            vendorId: vendor.id,
            region: selectedRegion,
            displayType: OfferDisplayType.FOR_SALE,
            type: selectedOfferType ?? undefined,
            sort: selectedSort,
            pageSize: VENDOR_DETAIL_OFFERS_MAX_PAGE_SIZE
        }),
        page: 1
    });

    const offers = queryData?.results ?? [];
    const pageCount = queryData ? Math.ceil(queryData.count / ACTIVE_OFFERS_TO_SHOW) : 0;

    const indexesToShow = useMemo(() => baseOffersArrayIndexesToShow.map((el) => el + ACTIVE_OFFERS_TO_SHOW * (page - 1)), [page, queryData]);

    const modalItems: IOfferModalOffer[] = offers;

    const handlePageChange = (newPage: number) => {
        dispatch(setActiveOffersPage(newPage));
        if (listRef.current) {
            const elementTop = listRef.current.getBoundingClientRect().top + window.scrollY;
            const offset = 100;
            window.scrollTo({top: elementTop - offset, behavior: "smooth"});
        }
    };

    const isLoading = offersInRegionRequest === RequestState.Waiting || isFetching;

    return (
        <>
            <div ref={listRef} className={cx(listContainer, isMounted && listContainerClientStyle)}>
                {isLoading && (
                    <div className={loadingOverlay}>
                        <Loader size="md" />
                    </div>
                )}
                <div className={isLoading ? listContentLoading : undefined}>
                    <ColumnList fluid>
                        {offers.map((offer, index) => (
                            <Col
                                key={offer.id}
                                sm={4}
                                md={4}
                                as="li"
                                className={cx(offerCol, isMounted && !indexesToShow.includes(index) && offerColClientStyle)}
                            >
                                <OfferBox
                                    offer={offer}
                                    onShowOfferDetailsBtnClick={() => {
                                        dispatch(showOfferModal({modalName, offerId: offer.id, offer}));
                                    }}
                                    unlinkVendor
                                    index={index}
                                    trackingMeta={{
                                        listName: "Aktywne oferty dewelopera",
                                        listId: "aktywne_oferty_dewelopera",
                                        source: "additional",
                                        tileEvents: {
                                            imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                            buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                            linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                            logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                                        }
                                    }}
                                />
                            </Col>
                        ))}
                    </ColumnList>
                </div>
            </div>

            {pageCount > 1 && (
                <div className={paginationHolder}>
                    <Pagination
                        currentPage={page}
                        size={isMobile ? PaginationSize.SM : PaginationSize.MD}
                        siblingsCount={isMobile ? 0 : 1}
                        pageCount={pageCount}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}

            <VendorDetailOfferModal vendorId={props.vendor.id} region={selectedRegion} items={modalItems} />
        </>
    );
});

const listContainer = css({
    position: "relative",
    //maxHeight values based of height of component with 6 elements displayed
    maxHeight: "2920px",
    overflow: "hidden",

    md: {
        maxHeight: "1060px"
    }
});

const listContainerClientStyle = css({
    maxHeight: "none",
    overflow: "unset",

    md: {
        maxHeight: "none"
    }
});

const loadingOverlay = css({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    zIndex: 10
});

const listContentLoading = css({
    opacity: 0.5,
    pointerEvents: "none"
});

const offerCol = css({
    display: "flex",
    justifyContent: "center"
});

const offerColClientStyle = css({
    display: "none !important"
});

const paginationHolder = css({
    display: "flex",
    justifyContent: "center",
    marginTop: 6
});
import React, {FunctionComponent, useMemo, useRef} from "react";
import {css, cx} from "@panda/css";

import {Col} from "@pg-design/grid-module";
import {Loader} from "@pg-design/loader-module";
import {Pagination, PaginationSize} from "@pg-design/pagination-module";
import {useIsMounted} from "@pg-mono/hooks";
import {RequestState} from "@pg-mono/request-state";
import {useUserDevice} from "@pg-mono/user-device";

import {ColumnList} from "../../../atoms/ColumnList";
import {OfferBox} from "../../../offer/components/offer_box/OfferBox";
import {OfferDisplayType} from "../../../offer/helpers/OfferDisplayType";
import {showOfferModal} from "../../../offer/state/offer_modals_slice";
import {offerBoxTracking} from "../../../offer/tracking/offer_box_gtm_events";
import {IOfferModalName} from "../../../offer/types/OfferModalName";
import {IOfferModalOffer} from "../../../offer/types/OfferModalOffer";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {useGetVendorDetailAllActiveOffersQuery} from "../../api/get_vendor_detail_all_active_offers";
import {VENDOR_DETAIL_OFFERS_MAX_PAGE_SIZE} from "../../constants/vendor_detail_offers_max_page_size";
import {setActiveOffersPage} from "../../state/vendor_offer_list_slice";
import {convertToVendorDetailViewOffersPayload} from "../../utils/convert_to_vendor_detail_view_offers_payload";
import {VendorDetailOfferModal} from "./VendorDetailOfferModal";

interface IProps {
    vendor: {
        id: number;
        stats: {
            offers_count_for_sale: number;
            properties_count_for_sale: number;
        };
    };
    offersInRegionRequest: RequestState;
}

const modalName: IOfferModalName = "mainOfferModal";

const ACTIVE_OFFERS_TO_SHOW = 6;
//array with index numbers, length is equal to ACTIVE_OFFERS_TO_SHOW
const baseOffersArrayIndexesToShow = Array.from(Array(ACTIVE_OFFERS_TO_SHOW).keys());

export const VendorDetailActiveOffers: FunctionComponent<IProps> = React.memo((props) => {
    const {offersInRegionRequest, vendor} = props;
    const dispatch = useAppDispatch();
    const {isMobile} = useUserDevice();
    const isMounted = useIsMounted();
    const listRef = useRef<HTMLDivElement>(null);

    const page = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.page);
    const selectedRegion = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.region);
    const selectedOfferType = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.offerType);
    const selectedSort = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.sort);

    const {data: queryData, isFetching} = useGetVendorDetailAllActiveOffersQuery({
        ...convertToVendorDetailViewOffersPayload({
            vendorId: vendor.id,
            region: selectedRegion,
            displayType: OfferDisplayType.FOR_SALE,
            type: selectedOfferType ?? undefined,
            sort: selectedSort,
            pageSize: VENDOR_DETAIL_OFFERS_MAX_PAGE_SIZE
        }),
        page: 1
    });

    const offers = queryData?.results ?? [];
    const pageCount = queryData ? Math.ceil(queryData.count / ACTIVE_OFFERS_TO_SHOW) : 0;

    const indexesToShow = useMemo(() => baseOffersArrayIndexesToShow.map((el) => el + ACTIVE_OFFERS_TO_SHOW * (page - 1)), [page, queryData]);

    const modalItems: IOfferModalOffer[] = offers;

    const handlePageChange = (newPage: number) => {
        dispatch(setActiveOffersPage(newPage));
        if (listRef.current) {
            const elementTop = listRef.current.getBoundingClientRect().top + window.scrollY;
            const offset = 100;
            window.scrollTo({top: elementTop - offset, behavior: "smooth"});
        }
    };

    const isLoading = offersInRegionRequest === RequestState.Waiting || isFetching;

    return (
        <>
            <div ref={listRef} className={cx(listContainer, isMounted && listContainerClientStyle)}>
                {isLoading && (
                    <div className={loadingOverlay}>
                        <Loader size="md" />
                    </div>
                )}
                <div className={isLoading ? listContentLoading : undefined}>
                    <ColumnList fluid>
                        {offers.map((offer, index) => (
                            <Col
                                key={offer.id}
                                sm={4}
                                md={4}
                                as="li"
                                className={cx(offerCol, isMounted && !indexesToShow.includes(index) && offerColClientStyle)}
                            >
                                <OfferBox
                                    offer={offer}
                                    onShowOfferDetailsBtnClick={() => {
                                        dispatch(showOfferModal({modalName, offerId: offer.id, offer}));
                                    }}
                                    unlinkVendor
                                    index={index}
                                    trackingMeta={{
                                        listName: "Aktywne oferty dewelopera",
                                        listId: "aktywne_oferty_dewelopera",
                                        source: "additional",
                                        tileEvents: {
                                            imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                            buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                            linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                            logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                                        }
                                    }}
                                />
                            </Col>
                        ))}
                    </ColumnList>
                </div>
            </div>

            {pageCount > 1 && (
                <div className={paginationHolder}>
                    <Pagination
                        currentPage={page}
                        size={isMobile ? PaginationSize.SM : PaginationSize.MD}
                        siblingsCount={isMobile ? 0 : 1}
                        pageCount={pageCount}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}

            <VendorDetailOfferModal vendorId={props.vendor.id} region={selectedRegion} items={modalItems} />
        </>
    );
});

const listContainer = css({
    position: "relative",
    //maxHeight values based of height of component with 6 elements displayed
    maxHeight: "2920px",
    overflow: "hidden",

    md: {
        maxHeight: "1060px"
    }
});

const listContainerClientStyle = css({
    maxHeight: "none",
    overflow: "unset",

    md: {
        maxHeight: "none"
    }
});

const loadingOverlay = css({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    zIndex: 10
});

const listContentLoading = css({
    opacity: 0.5,
    pointerEvents: "none"
});

const offerCol = css({
    display: "flex",
    justifyContent: "center"
});

const offerColClientStyle = css({
    display: "none !important"
});

const paginationHolder = css({
    display: "flex",
    justifyContent: "center",
    marginTop: 6
});
